import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
});

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const normalizePaginatedList = (payload) => {
  const data = payload?.data ?? payload;
  const list = Array.isArray(data) ? data : data?.data || [];
  const pagination = data?.pagination || {};

  return {
    items: Array.isArray(list) ? list : [],
    pagination: {
      page: pagination.page || data?.page || 1,
      limit: pagination.limit || data?.limit || 10,
      total: pagination.total || data?.total || 0,
      total_pages: pagination.total_pages || data?.total_pages || 1,
    },
  };
};

const buildListParams = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = "",
    organisationId,
    organisation_id,
  } = params;

  return {
    page,
    limit,
    search,
    organisation_id: organisationId || organisation_id,
  };
};

export const fetchBusinessUnits = createAsyncThunk(
  "businessUnits/fetchBusinessUnits",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await axios.get(`${BASE_URL}/all-business-units`, {
        headers: getHeaders(),
        signal,
        params: buildListParams(params),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch business units")
      );
    }
  }
);

export const fetchBusinessUnitById = createAsyncThunk(
  "businessUnits/fetchBusinessUnitById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/business-unit/${id}`, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch business unit")
      );
    }
  }
);

export const addBusinessUnit = createAsyncThunk(
  "businessUnits/addBusinessUnit",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/business-unit`, data, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Business unit add failed")
      );
    }
  }
);

export const updateBusinessUnit = createAsyncThunk(
  "businessUnits/updateBusinessUnit",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE_URL}/business-unit`, data, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Business unit update failed")
      );
    }
  }
);

export const deleteBusinessUnit = createAsyncThunk(
  "businessUnits/deleteBusinessUnit",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/business-unit/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Business unit delete failed")
      );
    }
  }
);

export const fetchLocationsUnits = createAsyncThunk(
  "businessUnits/fetchLocationsUnits",
  async (params = {}, { rejectWithValue, signal }) => {
    try {
      const res = await axios.get(`${BASE_URL}/all-locations-units`, {
        headers: getHeaders(),
        signal,
        params: buildListParams(params),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch locations")
      );
    }
  }
);

export const fetchLocationsUnitById = createAsyncThunk(
  "businessUnits/fetchLocationsUnitById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${BASE_URL}/locations-unit/${id}`, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch location")
      );
    }
  }
);

export const addLocationsUnit = createAsyncThunk(
  "businessUnits/addLocationsUnit",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${BASE_URL}/locations-unit`, data, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Location add failed"));
    }
  }
);

export const updateLocationsUnit = createAsyncThunk(
  "businessUnits/updateLocationsUnit",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${BASE_URL}/locations-unit`, data, {
        headers: getHeaders(),
      });

      return res.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Location update failed"));
    }
  }
);

export const deleteLocationsUnit = createAsyncThunk(
  "businessUnits/deleteLocationsUnit",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${BASE_URL}/locations-unit/${id}`, {
        headers: getHeaders(),
      });

      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, "Location delete failed"));
    }
  }
);

const listState = {
  items: [],
  page: 1,
  limit: 10,
  search: "",
  total: 0,
  totalPages: 1,
  loading: false,
};

const initialState = {
  ...listState,
  selectedBusinessUnit: null,
  locations: {
    ...listState,
    selectedLocation: null,
  },
  saving: false,
  deleting: false,
  error: null,
  successMessage: "",
};

const applyListState = (state, payload) => {
  const normalized = normalizePaginatedList(payload);

  state.loading = false;
  state.items = normalized.items;
  state.page = normalized.pagination.page;
  state.limit = normalized.pagination.limit;
  state.total = normalized.pagination.total;
  state.totalPages = normalized.pagination.total_pages;
};

const businessUnitSlice = createSlice({
  name: "businessUnits",
  initialState,
  reducers: {
    setBusinessUnitPage: (state, action) => {
      state.page = action.payload || 1;
    },
    setBusinessUnitSearch: (state, action) => {
      state.search = action.payload || "";
      state.page = 1;
    },
    setLocationsUnitPage: (state, action) => {
      state.locations.page = action.payload || 1;
    },
    setLocationsUnitSearch: (state, action) => {
      state.locations.search = action.payload || "";
      state.locations.page = 1;
    },
    clearBusinessUnitToast: (state) => {
      state.error = null;
      state.successMessage = "";
    },
    resetBusinessUnitState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessUnits.fulfilled, (state, action) => {
        applyListState(state, action.payload);
      })
      .addCase(fetchBusinessUnits.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.error = action.payload;
      })

      .addCase(fetchBusinessUnitById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBusinessUnitById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedBusinessUnit = action.payload?.data || action.payload;
      })
      .addCase(fetchBusinessUnitById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addBusinessUnit.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(addBusinessUnit.fulfilled, (state) => {
        state.saving = false;
        state.successMessage = "Business unit added successfully";
      })
      .addCase(addBusinessUnit.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateBusinessUnit.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(updateBusinessUnit.fulfilled, (state, action) => {
        state.saving = false;
        const updatedBusinessUnit = action.payload?.data || action.payload;

        if (updatedBusinessUnit?.id) {
          state.items = state.items.map((item) =>
            item.id === updatedBusinessUnit.id
              ? { ...item, ...updatedBusinessUnit }
              : item
          );
        }

        state.successMessage = "Business unit updated successfully";
      })
      .addCase(updateBusinessUnit.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(deleteBusinessUnit.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(deleteBusinessUnit.fulfilled, (state, action) => {
        state.deleting = false;
        state.items = state.items.filter((item) => item.id !== action.payload);
        state.successMessage = "Business unit deleted successfully";
      })
      .addCase(deleteBusinessUnit.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })

      .addCase(fetchLocationsUnits.pending, (state) => {
        state.locations.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationsUnits.fulfilled, (state, action) => {
        applyListState(state.locations, action.payload);
      })
      .addCase(fetchLocationsUnits.rejected, (state, action) => {
        state.locations.loading = false;
        if (action.meta.aborted) return;
        state.error = action.payload;
      })

      .addCase(fetchLocationsUnitById.pending, (state) => {
        state.locations.loading = true;
        state.error = null;
      })
      .addCase(fetchLocationsUnitById.fulfilled, (state, action) => {
        state.locations.loading = false;
        state.locations.selectedLocation =
          action.payload?.data || action.payload;
      })
      .addCase(fetchLocationsUnitById.rejected, (state, action) => {
        state.locations.loading = false;
        state.error = action.payload;
      })

      .addCase(addLocationsUnit.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(addLocationsUnit.fulfilled, (state) => {
        state.saving = false;
        state.successMessage = "Location added successfully";
      })
      .addCase(addLocationsUnit.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(updateLocationsUnit.pending, (state) => {
        state.saving = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(updateLocationsUnit.fulfilled, (state, action) => {
        state.saving = false;
        const updatedLocation = action.payload?.data || action.payload;

        if (updatedLocation?.id) {
          state.locations.items = state.locations.items.map((item) =>
            item.id === updatedLocation.id
              ? { ...item, ...updatedLocation }
              : item
          );
        }

        state.successMessage = "Location updated successfully";
      })
      .addCase(updateLocationsUnit.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(deleteLocationsUnit.pending, (state) => {
        state.deleting = true;
        state.error = null;
        state.successMessage = "";
      })
      .addCase(deleteLocationsUnit.fulfilled, (state, action) => {
        state.deleting = false;
        state.locations.items = state.locations.items.filter(
          (item) => item.id !== action.payload
        );
        state.successMessage = "Location deleted successfully";
      })
      .addCase(deleteLocationsUnit.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBusinessUnitToast,
  resetBusinessUnitState,
  setBusinessUnitPage,
  setBusinessUnitSearch,
  setLocationsUnitPage,
  setLocationsUnitSearch,
} = businessUnitSlice.actions;

export default businessUnitSlice.reducer;
